/**
 * One staged bank line in full: the fields the provider delivered and the
 * decisions taken on it. Read from the inbox and from a capital call.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Unlink, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useBankIncomingAudit,
  useUnlinkBankIncomingAllocation,
} from '@/hooks/react-query/useBankIncoming';
import type { BankIncomingScope } from '@/services/bankIncomingService';
import {
  BANK_TRANSACTION_TYPE_LABELS,
  RECONCILIATION_ACTION_LABELS,
  type BankIncomingAdminLine,
  type BankIncomingAuditEntry,
  type MatchedTarget,
  type ReconciliationAction,
} from '@/types/bank-reconciliation';
import { FUND_TRANSACTION_TYPE_LABELS } from '@/types/fund-transactions';
import { formatCurrency, formatDate } from '@/utils/formatters';

export function DetailRow({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-2 py-1.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground break-words">
        {value || '-'}
      </span>
    </div>
  );
}

/**
 * What this line has been used for, and the way back out of each placement.
 *
 * Unlinking reverses one allocation and the posting it made. The call's own
 * entry is untouched - it recognised the money when it was issued - and the
 * line comes back for that much and no more.
 */
export function IncomingLineAllocations({
  line,
  scope,
}: {
  line: BankIncomingAdminLine;
  scope?: BankIncomingScope;
}) {
  const unlink = useUnlinkBankIncomingAllocation(scope);

  if (line.allocations.length === 0) return null;

  const currency = line.currency ?? undefined;
  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <span className="text-sm font-medium text-foreground">Allocations</span>
        <span className="text-sm text-muted-foreground">
          {formatCurrency(line.allocated_amount, { currency })} placed ·{' '}
          {formatCurrency(line.unapplied_amount, { currency })} left
        </span>
      </div>
      <ul className="space-y-1">
        {line.allocations.map((allocation) => (
          <li
            key={allocation.id}
            className="flex items-center justify-between gap-3 border-b border-border py-1.5 last:border-0"
          >
            <span className="text-sm text-foreground">
              {formatCurrency(allocation.amount, { currency })}
              {allocation.resolution === 'UNAPPLIED' && (
                <span className="ml-2 text-xs text-amber-600">
                  {line.amount > 0
                    ? 'held as LP credit'
                    : 'refunded from LP credit'}
                </span>
              )}
              {allocation.resolution === 'PAIRED' && (
                <span className="ml-2 text-xs text-muted-foreground">
                  paired with an opposite bank line
                </span>
              )}
              {allocation.resolution === 'MANAGER_CURRENT_ACCOUNT' && (
                <span className="ml-2 text-xs text-muted-foreground">
                  manager current account
                </span>
              )}
              {allocation.resolution === 'RETURNED' && (
                <span className="ml-2 text-xs text-muted-foreground">
                  recorded as a returned payment
                </span>
              )}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatDate(allocation.created_at)}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={unlink.isPending}
              onClick={() =>
                unlink.mutate({
                  incomingId: line.id,
                  allocationId: allocation.id,
                })
              }
            >
              <Unlink className="h-3.5 w-3.5 mr-1" />
              Unlink
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Every field the provider delivered with this line. */
export function IncomingLineDetails({ line }: { line: BankIncomingAdminLine }) {
  return (
    <div className="space-y-1">
      <DetailRow label="Fund" value={line.fund_name} />
      <DetailRow
        label="Account"
        value={`${line.account_name} · ${line.bank_name}`}
      />
      <DetailRow label="Account IBAN" value={line.account_iban} />
      <DetailRow label="Value date" value={formatDate(line.value_date)} />
      <DetailRow
        label="Execution date"
        value={formatDate(line.execution_date)}
      />
      <DetailRow label="Counterparty" value={line.counterparty_name} />
      <DetailRow
        label="Counterparty reference"
        value={line.counterparty_reference}
      />
      <DetailRow
        label="Remittance information"
        value={line.remittance_information}
      />
      <DetailRow
        label="Remittance type"
        value={line.remittance_information_type}
      />
      <DetailRow label="End-to-end id" value={line.end_to_end_id} />
      <DetailRow
        label="Bank transaction type"
        value={BANK_TRANSACTION_TYPE_LABELS[line.bank_transaction_type]}
      />
      <DetailRow
        label="Bank transaction code"
        value={line.bank_transaction_code}
      />
      <DetailRow
        label="Proprietary code"
        value={line.proprietary_bank_transaction_code}
      />
      <DetailRow
        label="Fund transaction type"
        value={line.fund_transaction_type}
      />
      {line.matched_targets.length > 0 && (
        <DetailRow label="Matched to" value={<MatchedTargets line={line} />} />
      )}
      <DetailRow
        label="Match status"
        value={line.match_status === 'MATCHED' ? 'Matched' : 'Awaiting match'}
      />
      <DetailRow label="Source" value={line.source} />
      <DetailRow label="Provider id" value={line.external_id} />
      <DetailRow label="Received" value={formatDate(line.created_at)} />
    </div>
  );
}

// What the line settled: one fund transaction, or one per allocation when it
// was split across several.
function MatchedTargets({ line }: { line: BankIncomingAdminLine }) {
  if (line.matched_targets.length === 1) {
    return (
      <MatchedTargetLink
        fundId={line.fund_id}
        target={line.matched_targets[0]}
        currency={line.currency}
      />
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1 text-sm text-foreground hover:underline">
        {line.matched_targets.length} transactions
        <ChevronDown className="h-3 w-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-w-md">
        {line.matched_targets.map((target) => (
          <DropdownMenuItem
            key={`${target.fund_transaction_id}-${target.investor_name ?? ''}-${target.applied_amount}`}
            asChild
          >
            <Link
              to={`/app/funds/${line.fund_id}/fund-transactions/${target.fund_transaction_id}`}
              className="flex flex-col items-start gap-0.5"
            >
              <span>{targetTitle(target)}</span>
              <span className="text-xs text-muted-foreground">
                {targetSummary(target, line.currency)}
              </span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MatchedTargetLink({
  fundId,
  target,
  currency,
}: {
  fundId: string;
  target: MatchedTarget;
  currency: string | null;
}) {
  return (
    <span className="flex flex-col gap-0.5">
      <Link
        to={`/app/funds/${fundId}/fund-transactions/${target.fund_transaction_id}`}
        className="text-sm text-primary hover:underline"
      >
        {targetTitle(target)}
      </Link>
      <span className="text-xs text-muted-foreground">
        {targetSummary(target, currency)}
      </span>
    </span>
  );
}

function targetTitle(target: MatchedTarget): string {
  return (
    target.description ??
    FUND_TRANSACTION_TYPE_LABELS[target.fund_transaction_type]
  );
}

function targetSummary(target: MatchedTarget, currency: string | null): string {
  const money = (amount: number) =>
    formatCurrency(amount, { currency: currency ?? undefined });
  const applied =
    target.applied_amount === target.transaction_amount
      ? money(target.transaction_amount)
      : `${money(target.applied_amount)} of ${money(target.transaction_amount)}`;
  return [
    target.investor_name,
    FUND_TRANSACTION_TYPE_LABELS[target.fund_transaction_type],
    formatDate(target.transaction_date),
    applied,
  ]
    .filter(Boolean)
    .join(' · ');
}

// A suggestion the engine made before the line was settled says nothing any
// more: what it was matched to is shown above.
const SETTLING_ACTIONS: ReconciliationAction[] = [
  'AUTO_APPLIED',
  'ACCEPTED',
  'MANUALLY_MATCHED',
];

function withoutSupersededSuggestions(
  entries: BankIncomingAuditEntry[]
): BankIncomingAuditEntry[] {
  const settled = entries.findIndex((entry) =>
    SETTLING_ACTIONS.includes(entry.action)
  );
  if (settled < 0) return entries;
  return entries.filter(
    (entry, index) => entry.action !== 'SUGGESTED' || index < settled
  );
}

// The decision history of one line: what the batch proposed, what an operator
// did about it, and which engine and rule version produced it.
export function IncomingLineAuditTrail({
  incomingId,
  scope,
}: {
  incomingId: string;
  scope?: BankIncomingScope;
}) {
  const { data: entries, isLoading } = useBankIncomingAudit(incomingId, scope);

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">Loading decision history…</p>
    );
  }
  if (!entries || entries.length === 0) return null;

  const decisions = withoutSupersededSuggestions(entries);

  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        Decision history
      </p>
      <div className="space-y-2">
        {decisions.map((entry) => (
          <div
            key={entry.id}
            className="rounded-md border border-border px-3 py-2"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-foreground">
                {RECONCILIATION_ACTION_LABELS[entry.action]}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatDate(entry.created_at)} · engine {entry.engine_version}
                {entry.rule_version ? ` · rule v${entry.rule_version}` : ''}
              </span>
            </div>
            {entry.explanation && (
              <p className="text-sm text-muted-foreground mt-1">
                {entry.explanation}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
